// Shared formatters lifted from the analytics frontend. Pure utility functions
// with no Tailwind/Ant Design dependency — used inside session detail tabs.

export function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}m ${sec}s`;
}

export function formatSec(sec: number): string {
  if (sec < 60) return `${Math.round(sec)}s`;
  const min = Math.floor(sec / 60);
  const remSec = Math.round(sec % 60);
  return `${min}m ${remSec}s`;
}

export function formatPrice(val: number | string): string {
  return `$${Number(val).toFixed(2)}`;
}

export function formatRate(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function formatPlaybackTime(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, "0")}`;
}

export function formatRelativeTime(ts: number, start: number): string {
  const diff = ts - start;
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  return `+${min}:${String(remSec).padStart(2, "0")}`;
}

export function formatLocalTime(ts: string | null): string {
  if (!ts) return "--";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function formatLocalDate(ts: string): string {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export function formatLocalDateTime(ts: string): string {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "--";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function endReasonColor(reason: string): string {
  switch (reason) {
    case "order_complete":
      return "text-green-600 bg-green-50";
    case "timeout":
      return "text-orange-600 bg-orange-50";
    case "cancel":
      return "text-red-600 bg-red-50";
    default:
      return "text-gray-600 bg-gray-50";
  }
}

export function statusColor(status: string): string {
  switch (status) {
    case "COMPLETED":
      return "text-green-600";
    case "ABANDONED":
      return "text-red-600";
    default:
      return "text-gray-600";
  }
}

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  DKK: "kr",
  SEK: "kr",
  NOK: "kr",
};

export function formatMoney(value: number | string, currency?: string): string {
  const symbol = currency ? CURRENCY_SYMBOL[currency.toUpperCase()] ?? `${currency} ` : "$";
  const n = Number(value);
  if (!isFinite(n)) return `${symbol}0.00`;
  return `${symbol}${n.toFixed(2)}`;
}

export function formatLatencyMs(ms: number): string {
  if (!isFinite(ms) || ms < 0) return "--";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`;
  const min = Math.floor(ms / 60_000);
  const sec = Math.round((ms % 60_000) / 1000);
  return `${min}m ${sec}s`;
}

type CardBrandInfo = { brand: string; last4: string };

const BRAND_FROM_SUBTYPE: Record<string, string> = {
  AXC: "Amex",
  AMEX: "Amex",
  VIC: "Visa",
  VISA: "Visa",
  MCC: "Mastercard",
  MASTERCARD: "Mastercard",
  MC: "Mastercard",
  DISC: "Discover",
  DISCOVER: "Discover",
  DANKORT: "Dankort",
  GIFTCARD: "Gift Card",
  DEBIT: "Debit",
};

function brandFromBin(bin: string): string {
  if (!bin) return "Card";
  const n4 = bin.slice(0, 4);
  const n2 = bin.slice(0, 2);
  const n1 = bin[0];
  if (n2 === "34" || n2 === "37") return "Amex";
  if (n1 === "4") return "Visa";
  const n2Num = Number(n2);
  if (n2Num >= 51 && n2Num <= 55) return "Mastercard";
  if (n4 === "6011" || n2 === "65") return "Discover";
  if (n2Num >= 22 && n2Num <= 27) return "Mastercard";
  return "Card";
}

export function maskCardNumber(cardMask: string | undefined, subType?: string): CardBrandInfo {
  const fallbackBrand = subType ? BRAND_FROM_SUBTYPE[subType.toUpperCase()] ?? "Card" : "Card";
  if (!cardMask) return { brand: fallbackBrand, last4: "" };
  const digitsOnly = cardMask.replace(/\D/g, "");
  const last4 = digitsOnly.slice(-4);
  const bin = digitsOnly.slice(0, 6);
  const brand = subType ? BRAND_FROM_SUBTYPE[subType.toUpperCase()] ?? brandFromBin(bin) : brandFromBin(bin);
  return { brand, last4 };
}

export function entryModeLabel(code: string | undefined | null): string {
  switch ((code || "").toUpperCase()) {
    case "T":
      return "Tap (Contactless)";
    case "I":
      return "Insert (Chip)";
    case "S":
      return "Swipe (Magstripe)";
    case "M":
      return "Manual Entry";
    default:
      return code ? `Entry: ${code}` : "Unknown";
  }
}

export async function copyToClipboard(text: string): Promise<void> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // fall through
    }
  }
  if (typeof document === "undefined") return;
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(ta);
  }
}
